// Common export-trigger button group.
//
// Each entry generates one POST to its endpoint and opens the resulting
// presigned URL in a new tab. Per-button loading state surfaces in the
// Button's `loading` prop. Errors are surfaced via the toast bus when one
// is attached.

import { Button, useToast } from '@ohear/ui'
import type { ButtonSize, ButtonVariant } from '@ohear/ui'
import { useState } from 'react'
import { ApiError } from '../../lib/api'
import { type ExportFormat, type ExportResponse, triggerExport } from '../../lib/api/exports'

export interface ExportItem {
  /** Visible button label. */
  label: string
  /** File format, used for aria-labels and download filename hints. */
  format: ExportFormat
  /** API endpoint, e.g. "/api/v1/exports/cue-sheet". */
  endpoint: string
  /** Querystring params handed to the endpoint. */
  params: Record<string, string | number>
}

export interface ExportButtonsProps {
  exports: ReadonlyArray<ExportItem>
  variant?: ButtonVariant
  size?: ButtonSize
  /** Optional callback after a successful download fires. */
  onSuccess?: (item: ExportItem, response: ExportResponse) => void
}

export function ExportButtons({
  exports,
  variant = 'ghost',
  size = 'sm',
  onSuccess,
}: ExportButtonsProps) {
  const [loadingLabel, setLoadingLabel] = useState<string | null>(null)
  const toast = useToast()

  const handleExport = async (item: ExportItem) => {
    setLoadingLabel(item.label)
    try {
      const response = await triggerExport(item.endpoint, item.params)
      // window.open in a new tab keeps the SPA state intact; the URL itself
      // returns an attachment-disposition response from R2 so the browser
      // triggers a download.
      window.open(response.url, '_blank', 'noopener')
      onSuccess?.(item, response)
    } catch (err) {
      const description = err instanceof ApiError ? err.message : 'Please try again in a moment.'
      toast.show({
        severity: 'error',
        title: `Couldn't generate ${item.label.toLowerCase()}`,
        description,
      })
    } finally {
      setLoadingLabel(null)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Export options">
      {exports.map((item) => (
        <Button
          key={`${item.endpoint}:${item.label}`}
          size={size}
          variant={variant}
          type="button"
          loading={loadingLabel === item.label}
          disabled={loadingLabel !== null && loadingLabel !== item.label}
          aria-label={`Export ${item.label}`}
          onClick={() => void handleExport(item)}
        >
          {item.label}
        </Button>
      ))}
    </div>
  )
}
